import React, { useState } from "react";
import { Button, Grid, Typography } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import type { FieldValues, UseFormReturn } from "react-hook-form/dist/types";
import type { DefaultValues } from "react-hook-form/dist/types/form";
import { v4 as uuidv4 } from "uuid";
import { ErrorAlert } from "../alert/ErrorAlert";
import { LoadingSubmitButton } from "./LoadingSubmitButton";

type SubmittableFormLayoutProps<T extends FieldValues> = {
  header: string;
  subHeading?: string;
  submitActionLabel?: string;
  cancelLabel?: string;
  formButtons?: boolean;
  submitAction: (data: T) => Promise<void> | void;
  children: (formController: UseFormReturn<T>, isEditing?: boolean) => React.ReactNode;
  defaultValues?: DefaultValues<T>;
  forExistingEntity?: boolean;
};

export function SubmittableFormLayout<T extends FieldValues>(props: SubmittableFormLayoutProps<T>) {
  const {
    header,
    subHeading,
    submitActionLabel,
    cancelLabel,
    submitAction,
    children,
    defaultValues,
    forExistingEntity,
  } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<unknown>(undefined);
  const [isEditing, setIsEditing] = useState(false);

  const formController = useForm<T>({ mode: "onSubmit", defaultValues, shouldUnregister: true });

  const formId = uuidv4();

  const onSubmit = async (data: T) => {
    try {
      setIsLoading(true);

      // The UnpackNestedValue type doesn't play well with Luxon's DateTime type so if we cast as the generic type everything works
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      await submitAction(data as T);
    } catch (ex) {
      setApiError(ex);
    } finally {
      setIsLoading(false);
    }
  };

  const onEdit = () => {
    setIsEditing(true);
  };

  const onCancel = () => {
    setIsEditing(false);
    formController.reset();
  };

  const submitLabel = submitActionLabel || (!forExistingEntity ? "Save" : "Update");
  const isEditable = isEditing || !forExistingEntity;
  const { isDirty, isValid } = formController.formState;

  return (
    <Grid container spacing={3}>
      <Grid
        size={6}
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <Typography variant={"h5"}>{header}</Typography>
        {subHeading && <h6>{subHeading}</h6>}
      </Grid>
      <Grid
        size={6}
        sx={{
          textAlign: "right",
        }}
      >
        {!isEditing &&
          forExistingEntity && ( // Only show if currently not in editing state and it's for an existing entity
            <Button variant={"outlined"} color={"primary"} onClick={onEdit} sx={{ mr: 2 }}>
              Edit
            </Button>
          )}
        {isEditing &&
          forExistingEntity && ( // Only show if currently in editing state and it's for an existing entity
            <Button variant={"outlined"} color={"primary"} onClick={onCancel} sx={{ mr: 2 }}>
              {cancelLabel || "Cancel"}
            </Button>
          )}

        {isEditable && ( // Show if the form is currently been edited or if it's not for an existing entity
          <LoadingSubmitButton
            color={"success"}
            label={submitLabel}
            isLoading={isLoading}
            isDisabled={!isDirty}
            formName={formId}
          />
        )}
      </Grid>
      <Grid size={12}>
        <FormProvider {...formController}>
          <form onSubmit={formController.handleSubmit(onSubmit)} id={`form_${formId}`}>
            <Grid container spacing={3}>
              <>
                {!!apiError && (
                  <Grid size={12}>
                    <ErrorAlert error={apiError} />
                  </Grid>
                )}
                <Grid size={12}>{children(formController, isEditable)}</Grid>
              </>
            </Grid>
          </form>
        </FormProvider>
      </Grid>
    </Grid>
  );
}
